import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken, clearToken } from './App';
import i18n from './i18n';
import Layout from './Layout';
import { Ico } from './icons';


function Reports() {
    const { t } = useTranslation();
    // Fechas en horario LOCAL (toISOString usaría UTC y desplaza 'hoy' por la tarde)
    const _fmtLocal = (d) => (
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    );
    const today = _fmtLocal(new Date());
    const firstOfMonth = new Date();
    firstOfMonth.setDate(1);
    const firstStr = _fmtLocal(firstOfMonth);

    const [startDate, setStartDate] = useState(firstStr);
    const [endDate, setEndDate] = useState(today);
    const [employeeId, setEmployeeId] = useState('');
    const [groupBy, setGroupBy] = useState('employee');
    const [employees, setEmployees] = useState([]);
    const [data, setData] = useState([]);
    const [entries, setEntries] = useState([]);
    const [entriesTruncated, setEntriesTruncated] = useState(false);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(null);
    const [applied, setApplied] = useState(false);
    const navigate = useNavigate();

    const authHeaders = () => ({
        'Authorization': `Bearer ${getToken()}`,
    });

    useEffect(() => {
        fetch('/api/employees?include_inactive=true', {
            headers: authHeaders(),
        })
            .then((res) => {
                if (res.status === 401) {
                    clearToken();
                    localStorage.removeItem('nexus_user');
                    navigate('/login', { replace: true });
                    throw new Error(t('common.session_expired'));
                }
                return res.json();
            })
            .then((d) => setEmployees(d.employees || []))
            .catch(() => {});
    }, [navigate, t]);

    const runReport = useCallback(async () => {
        setError('');
        setLoading(true);
        setApplied(true);
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                group_by: groupBy,
            });
            if (employeeId) params.append('employee_id', employeeId);

            const res = await fetch(`/api/reports?${params}`, {
                headers: authHeaders(),
            });
            const d = await res.json();
            if (res.status === 401) {
                clearToken();
                localStorage.removeItem('nexus_user');
                navigate('/login', { replace: true });
                return;
            }
            if (!res.ok) {
                setError(d.error || t('common.http_error', { status: res.status }));
                setData([]);
                setEntries([]);
                setSummary(null);
                return;
            }
            setData(d.data || []);
            setEntries(d.entries || []);
            setEntriesTruncated(!!d.entries_truncated);
            setSummary(d.summary || null);
        } catch (err) {
            setError(t('common.network_error', { message: err.message }));
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, employeeId, groupBy, navigate, t]);

    useEffect(() => {
        runReport();
    }, []);

    const download = async (format) => {
        setError('');
        setDownloading(format);
        try {
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                group_by: groupBy,
                format,
            });
            if (employeeId) params.append('employee_id', employeeId);

            const res = await fetch(`/api/reports/download?${params}`, {
                headers: authHeaders(),
            });
            if (res.status === 401) {
                clearToken();
                localStorage.removeItem('nexus_user');
                navigate('/login', { replace: true });
                return;
            }
            if (!res.ok) {
                let msg = t('common.http_error', { status: res.status });
                try { const j = await res.json(); msg = j.error || msg; } catch {}
                setError(msg);
                return;
            }
            const blob = await res.blob();
            const cd = res.headers.get('Content-Disposition') || '';
            const m = cd.match(/filename="?([^";]+)"?/);
            const filename = m ? m[1] : `reporte.${format}`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(t('common.network_error', { message: err.message }));
        } finally {
            setDownloading(null);
        }
    };

    const localeMap = { es: 'es-ES', ca: 'ca-ES', en: 'en-US' };
    const locale = localeMap[i18n.language?.split('-')[0]] || 'es-ES';

    const formatTime = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString(locale, {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    const fmtTime = (v) => {
        if (v === undefined || v === null || v === '') return '—';
        return String(v);
    };

    const SOURCE_LABELS = { nfc: 'NFC', web: 'Web' };
    const sourceLabel = (s) => SOURCE_LABELS[s] || s || '—';

    const GROUP_LABELS = {
        employee: t('reports.group_employee'),
        day: t('reports.group_day'),
        day_only: t('reports.group_day_only'),
    };
    const fmtGroupBy = (g) => GROUP_LABELS[g] || g || '—';

    const hasDayCol = groupBy === 'day' || groupBy === 'day_only';

    return (
        <Layout title={t('common.reports')} subtitle={t('reports.subtitle')}>
            <section className="card">
                <div className="card-head">
                    <div>
                        <div className="card-title">{t('reports.filters_title')}</div>
                        <div className="card-sub">{t('reports.filters_sub', 'Define el rango y cómo agrupar los datos')}</div>
                    </div>
                </div>

                <div className="filters-grid">
                    <div className="field">
                        <label className="field-label" htmlFor="rep-start">{t('reports.filter_start')}</label>
                        <div className="control">
                            <span className="control-ico"><Ico name="calendar" size={16} /></span>
                            <input
                                id="rep-start"
                                className="input pad-ico"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label" htmlFor="rep-end">{t('reports.filter_end')}</label>
                        <div className="control">
                            <span className="control-ico"><Ico name="calendar" size={16} /></span>
                            <input
                                id="rep-end"
                                className="input pad-ico"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label" htmlFor="rep-employee">{t('reports.filter_employee')}</label>
                        <div className="control">
                            <span className="control-ico"><Ico name="user" size={16} /></span>
                            <select
                                id="rep-employee"
                                className="input pad-ico"
                                value={employeeId}
                                onChange={(e) => setEmployeeId(e.target.value)}
                            >
                                <option value="">{t('reports.filter_employee_all')}</option>
                                {employees.map((emp) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.full_name} {!emp.active && t('reports.filter_employee_inactive')}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="field">
                        <label className="field-label" htmlFor="rep-group">{t('reports.filter_group_by')}</label>
                        <div className="control">
                            <span className="control-ico"><Ico name="table2" size={16} /></span>
                            <select
                                id="rep-group"
                                className="input pad-ico"
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value)}
                            >
                                <option value="employee">{t('reports.group_employee')}</option>
                                <option value="day">{t('reports.group_day')}</option>
                                <option value="day_only">{t('reports.group_day_only')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="toolbar" style={{ marginTop: 18, marginBottom: 0 }}>
                    <button onClick={runReport} disabled={loading} className="btn btn-primary">
                        {loading
                            ? <><Ico name="refresh" size={15} />{t('reports.consulting')}</>
                            : <><Ico name="zap" size={15} />{t('reports.apply_filters')}</>}
                    </button>
                    <button
                        onClick={() => download('xlsx')}
                        disabled={downloading !== null}
                        className="btn btn-success"
                        title={t('reports.download_xlsx_title')}
                    >
                        {downloading === 'xlsx'
                            ? <><Ico name="refresh" size={15} />{t('reports.generating')}</>
                            : <><Ico name="file" size={15} />{t('reports.download_xlsx_plain', 'XLSX')}</>}
                    </button>
                    <button
                        onClick={() => download('pdf')}
                        disabled={downloading !== null}
                        className="btn btn-danger-soft"
                        title={t('reports.download_pdf_title')}
                    >
                        {downloading === 'pdf'
                            ? <><Ico name="refresh" size={15} />{t('reports.generating')}</>
                            : <><Ico name="file" size={15} />{t('reports.download_pdf_plain', 'PDF')}</>}
                    </button>
                </div>
            </section>

            {error && (
                <section className="card" style={{ padding: 0, border: 0, background: 'transparent', boxShadow: 'none' }}>
                    <div className="alert alert-error" role="alert">
                        <Ico name="alert" size={17} />
                        <span>{error}</span>
                    </div>
                </section>
            )}

            {summary && (
                <div className="stat-grid">
                    <div className="stat-card">
                        <div className="stat-ico tint-info"><Ico name="users" size={19} /></div>
                        <div>
                            <div className="stat-val num">{summary.total_employees}</div>
                            <div className="stat-lab">{t('reports.summary_employees')}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-ico tint-accent"><Ico name="activity" size={19} /></div>
                        <div>
                            <div className="stat-val num">{summary.total_entries}</div>
                            <div className="stat-lab">{t('reports.summary_clockings')}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-ico tint-success"><Ico name="clock" size={19} /></div>
                        <div>
                            <div className="stat-val num">{fmtTime(summary.total_time)}</div>
                            <div className="stat-lab">{t('reports.summary_total_time')}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-ico tint-warn"><Ico name="table2" size={19} /></div>
                        <div>
                            <div className="stat-val" style={{ fontSize: '1.05rem', lineHeight: 1.5 }}>{fmtGroupBy(summary.group_by)}</div>
                            <div className="stat-lab">{t('reports.summary_grouped_by')}</div>
                        </div>
                    </div>
                </div>
            )}

            {applied && !loading && (
                <section className="card">
                    <div className="card-head">
                        <div>
                            <div className="card-title">
                                {t('reports.results_title', {
                                    count: data.length,
                                    unit: data.length === 1 ? t('reports.results_unit_single') : t('reports.results_unit_plural'),
                                })}
                            </div>
                            <div className="card-sub">{t('reports.results_sub')}</div>
                        </div>
                        <div className="card-tools">
                            <span className="chip-code">{startDate} → {endDate}</span>
                        </div>
                    </div>

                    {data.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-ico"><Ico name="inbox" size={27} /></div>
                            <div className="empty-title">{t('reports.empty_title', 'Sin resultados')}</div>
                            <div className="empty-sub">{t('reports.no_data')}</div>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="nx-table">
                                <thead>
                                    <tr>
                                        <th>{t('reports.table_employee')}</th>
                                        {hasDayCol && <th>{t('reports.table_date')}</th>}
                                        <th>{t('reports.table_total_time')}</th>
                                        <th>{t('reports.table_entries_count')}</th>
                                        <th>{t('reports.table_first_in')}</th>
                                        <th>{t('reports.table_last_out')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((d, idx) => (
                                        <tr key={`${d.employee_id || 'all'}-${d.date || idx}`}>
                                            <td>
                                                <div className="emp-cell">
                                                    <div className="avatar sm">
                                                        {(d.employee_name || t('reports.all_employees')).trim().slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div className="emp-meta">
                                                        <div className="emp-name">{d.employee_name || t('reports.all_employees')}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            {hasDayCol && <td className="num">{d.date || '—'}</td>}
                                            <td><strong className="num" style={{ color: 'var(--accent)' }}>{fmtTime(d.total_time)}</strong></td>
                                            <td className="num">{d.total_entries}</td>
                                            <td className="num">{formatTime(d.first_clock_in)}</td>
                                            <td className="num">{formatTime(d.last_clock_out)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                {summary && (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={hasDayCol ? 2 : 1}>{t('reports.totals')}</td>
                                            <td>{fmtTime(summary.total_time)}</td>
                                            <td>{summary.total_entries}</td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </section>
            )}

            {applied && !loading && (
                <section className="card">
                    <div className="card-head">
                        <div>
                            <div className="card-title">{t('reports.detail_title', { count: entries.length })}</div>
                            <div className="card-sub">{t('reports.detail_sub')}</div>
                        </div>
                        <div className="card-tools">
                            <span className="chip-code">{startDate} → {endDate}</span>
                        </div>
                    </div>

                    {entries.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-ico"><Ico name="inbox" size={27} /></div>
                            <div className="empty-title">{t('reports.empty_title')}</div>
                            <div className="empty-sub">{t('reports.detail_empty')}</div>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="nx-table">
                                <thead>
                                    <tr>
                                        <th>{t('reports.table_employee')}</th>
                                        <th>{t('reports.detail_in')}</th>
                                        <th>{t('reports.detail_out')}</th>
                                        <th>{t('reports.table_total_time')}</th>
                                        <th>{t('dashboard.table_source')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((e, idx) => (
                                        <tr key={`${e.employee_name || 'x'}-${e.clock_in}-${idx}`}>
                                            <td>
                                                <div className="emp-cell">
                                                    <div className="avatar sm">
                                                        {(e.employee_name || '?').trim().slice(0, 2).toUpperCase()}
                                                    </div>
                                                    <div className="emp-meta">
                                                        <div className="emp-name">{e.employee_name || '—'}</div>
                                                        <div className="emp-mail">{e.date || ''}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="num">{formatTime(e.clock_in)}</td>
                                            <td className="num">
                                                {e.clock_out
                                                    ? formatTime(e.clock_out)
                                                    : <span className="badge badge-success"><span className="dot pulse" />{t('reports.detail_in_progress')}</span>}
                                            </td>
                                            <td><strong className="num" style={{ color: 'var(--accent)' }}>{fmtTime(e.duration_time)}</strong></td>
                                            <td className="num">{sourceLabel(e.source)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {entriesTruncated && (
                        <div className="alert alert-info" style={{ margin: '12px 18px 18px' }}>
                            <Ico name="alert" size={16} />
                            <span>{t('reports.detail_truncated')}</span>
                        </div>
                    )}
                </section>
            )}
        </Layout>
    );
}

export default Reports;
